import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { propertyOffersService } from "@/services/propertyOffersService";
import type { PropertyOffer, OfferStats } from "@/types/buyer";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook for buyers to manage their offers
 */
export const useBuyerOffers = () => {
  const { buyerProfile } = useBuyer();
  const [offers, setOffers] = useState<PropertyOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<OfferStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    withdrawn: 0,
    cancelled: 0,
  });

  useEffect(() => {
    if (!buyerProfile?.id) {
      setIsLoading(false);
      return;
    }

    const fetchOffers = async () => {
      setIsLoading(true);
      const data = await propertyOffersService.getOffersForBuyer(buyerProfile.id);
      setOffers(data);
      
      // Calculate stats
      const newStats = {
        total: data.length,
        pending: data.filter(o => o.status === 'pending').length,
        accepted: data.filter(o => o.status === 'accepted').length,
        rejected: data.filter(o => o.status === 'rejected').length,
        withdrawn: data.filter(o => o.status === 'withdrawn').length,
        cancelled: data.filter(o => o.status === 'cancelled').length,
      };
      setStats(newStats);
      setIsLoading(false);
    };

    fetchOffers();

    // Subscribe to real-time updates with Safari protection
    let channel: ReturnType<typeof supabase.channel> | null = null;
    
    try {
      channel = supabase
        .channel('buyer-offers')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_offers',
            filter: `buyer_id=eq.${buyerProfile.id}`,
          },
          () => {
            fetchOffers();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to buyer offers (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) {
          channel.unsubscribe();
        }
      } catch (error) {
        console.warn('Error cleaning up buyer offers subscription:', error);
      }
    };
  }, [buyerProfile?.id]);

  const withdrawOffer = async (offerId: string) => {
    const { error } = await propertyOffersService.withdrawOffer(offerId);
    if (!error && buyerProfile?.id) {
      const data = await propertyOffersService.getOffersForBuyer(buyerProfile.id);
      setOffers(data);
    }
    return { error };
  };

  return {
    offers,
    stats,
    isLoading,
    withdrawOffer,
  };
};

/**
 * Hook for sellers to manage offers on their properties
 */
export const useSellerOffers = (sellerId?: string) => {
  const { user } = useAuth();
  const effectiveSellerId = sellerId || user?.id;
  
  const [offers, setOffers] = useState<PropertyOffer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<OfferStats>({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    withdrawn: 0,
    cancelled: 0,
  });

  useEffect(() => {
    if (!effectiveSellerId) {
      setIsLoading(false);
      return;
    }

    const fetchOffers = async () => {
      setIsLoading(true);
      const data = await propertyOffersService.getOffersForSeller(effectiveSellerId);
      setOffers(data);
      
      // Calculate stats
      const newStats = {
        total: data.length,
        pending: data.filter(o => o.status === 'pending').length,
        accepted: data.filter(o => o.status === 'accepted').length,
        rejected: data.filter(o => o.status === 'rejected').length,
        withdrawn: data.filter(o => o.status === 'withdrawn').length,
        cancelled: data.filter(o => o.status === 'cancelled').length,
      };
      setStats(newStats);
      setIsLoading(false);
    };

    fetchOffers();

    // Subscribe to real-time updates with Safari protection
    let channel: ReturnType<typeof supabase.channel> | null = null;
    
    try {
      channel = supabase
        .channel('seller-offers')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'property_offers',
            filter: `seller_id=eq.${effectiveSellerId}`,
          },
          () => {
            fetchOffers();
          }
        )
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to seller offers (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (channel) {
          channel.unsubscribe();
        }
      } catch (error) {
        console.warn('Error cleaning up seller offers subscription:', error);
      }
    };
  }, [effectiveSellerId]);

  const acceptOffer = async (offerId: string, sellerResponse?: string) => {
    const { error } = await propertyOffersService.acceptOffer(offerId, sellerResponse);
    if (!error && effectiveSellerId) {
      const data = await propertyOffersService.getOffersForSeller(effectiveSellerId);
      setOffers(data);
    }
    return { error };
  };

  const rejectOffer = async (offerId: string, sellerResponse?: string) => {
    const { error } = await propertyOffersService.rejectOffer(offerId, sellerResponse);
    if (!error && effectiveSellerId) {
      const data = await propertyOffersService.getOffersForSeller(effectiveSellerId);
      setOffers(data);
    }
    return { error };
  };

  return {
    offers,
    stats,
    isLoading,
    acceptOffer,
    rejectOffer,
  };
};
